import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { login } from '../services/api';
import { resolvePostLoginRoute } from '../orbit/lib/postLoginRoute';
import { clearAuthState } from '../services/authStorage';
import { AuthShell, FIELD, FOOTNOTE, GoogleMark, LABEL, OrDivider, PasswordField, PRIMARY, QUIET } from './auth/AuthShell';

/*
 * Sign in, on the split screen it shares with sign up. The logic is unchanged: the
 * token and the user's facts land in localStorage, then the post-login route
 * decides where this person's work starts (the owner's is Adoption).
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface LoginUser {
  id: string;
  role: string;
  email?: string;
  name?: string;
  client_id?: string | null;
  is_provider?: boolean;
  is_provider_owner?: boolean;
  is_builder_admin?: boolean;
  is_expert?: boolean;
  provider_id?: string | null;
  is_main_provider?: boolean;
}

interface LoginResponse { token?: string; refresh_token?: string; is_new?: boolean; user?: LoginUser; }

const flag = (on: boolean | undefined): string => {
  if (on) return '1';
  return '';
};

const BAD_CREDENTIALS = 'Email or password is not right. Check both and try again.';

const LOGIN_ERRORS: Record<string, string> = {
  google_mismatch: 'That Google account is not the one your invite was sent to. Sign in with the Google account for your invited email.',
  invite_invalid: 'That invite link is used or expired. Ask the person who invited you for a new one.',
  intake_link_used: 'That link has already been used. Sign in with your email, or use Forgot it? to set a password.',
  company_exists: 'Your company is already on Agents Anywhere. Ask its owner to invite you, then sign in with that email.',
  account_inactive: 'This account is switched off. Ask your company owner to add you back.',
  workspace_missing: 'Your account is not linked to a workspace. Contact support and we will reconnect it.',
  oauth_state: 'Google sign-in timed out. Try again.',
  oauth_failed: 'Google sign-in did not finish. Try again, or sign in with your email.',
};

const loginErrorFor = (err: unknown): string => {
  if (!axios.isAxiosError(err)) return BAD_CREDENTIALS;
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string' && LOGIN_ERRORS[detail]) return LOGIN_ERRORS[detail];
  return BAD_CREDENTIALS;
};

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(() => {
    const code = new URLSearchParams(window.location.search).get('error') || '';
    return LOGIN_ERRORS[code] || '';
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      const response = (await login(email, password)) as LoginResponse;
      if (!response.token || !response.user) {
        setError('That did not sign you in. Try again.');
        return;
      }
      const user = response.user;
      clearAuthState();
      localStorage.setItem('auth_token', response.token);
      if (response.refresh_token) localStorage.setItem('refresh_token', response.refresh_token);
      localStorage.setItem('user_role', user.role);
      localStorage.setItem('user_id', user.id);
      localStorage.setItem('is_provider', flag(user.is_provider));
      localStorage.setItem('is_provider_owner', flag(user.is_provider_owner));
      localStorage.setItem('is_builder_admin', flag(user.is_builder_admin));
      localStorage.setItem('is_expert', flag(user.is_expert));
      localStorage.setItem('provider_id', user.provider_id || '');
      localStorage.setItem('is_main_provider', flag(user.is_main_provider));
      if (user.client_id) localStorage.setItem('client_id', user.client_id);
      if (user.email) localStorage.setItem('user_email', user.email);
      if (user.name) localStorage.setItem('user_name', user.name);
      toast.success('Signed in.');
      // is_new is true only on a client's first sign-in to an account made
      // for them, which then walks the setup like a sign-up does.
      const target = await resolvePostLoginRoute(user.role, user.client_id ?? null, response.is_new === true);
      window.location.href = target;
    } catch (err) {
      setError(loginErrorFor(err));
    } finally {
      setIsLoading(false);
    }
  };

  let submitLabel = 'Sign in';
  if (isLoading) submitLabel = 'Signing in';

  return (
    <AuthShell
      tab="login"
      title="Welcome back"
      sub="Sign in with the email your company knows you by."
    >
      <button type="button" onClick={() => { window.location.href = `${API_BASE_URL}/api/auth/google`; }} className={QUIET} data-testid="login-google">
        <GoogleMark />
        Continue with Google
      </button>
      <OrDivider />
      <form onSubmit={handleSubmit} className="flex flex-col gap-5" noValidate>
        <div>
          <label htmlFor="login-email" className={LABEL}>Work email</label>
          <input id="login-email" data-testid="login-email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" required className={FIELD} />
        </div>
        <PasswordField
          id="login-password"
          testId="login-password"
          label="Password"
          value={password}
          onChange={setPassword}
          placeholder="Your password"
          autoComplete="current-password"
          trailing={<Link to="/forgot-password" className="text-[13px] font-semibold text-[#666971] underline underline-offset-4 hover:text-[#18191d]">Forgot it?</Link>}
        />
        {error && <p role="alert" className="m-0 rounded-md border border-[#e8b4b4] bg-[#fdf3f3] px-3 py-2 text-[13px] text-[#8a2b2b]">{error}</p>}
        <button type="submit" disabled={isLoading || !email || !password} className={`${PRIMARY} mt-1`} data-testid="login-submit">{submitLabel}</button>
      </form>
      <p className={FOOTNOTE}>Invited by a colleague? Use the link in their email.</p>
    </AuthShell>
  );
};

export default Login;
